/**
 * One extracted line → one `create_enquiry` row.
 *
 * Split out of `CreateEnquiryBar.tsx` so it can be tested: this repo's vitest runs in a `node`
 * environment with no DOM, so a pure function is the only part of the bar that can be covered at
 * all. The decision below is also the one most worth covering.
 *
 * ---- `uom` is mandatory, so a null cannot be sent ----
 *
 * Measured live against the real backend:
 *
 *     POST create_enquiry {…items:[{raw_text:"…", qty:5, uom:null, item_code:null}]}
 *     → MandatoryError: [Enquiry, ENQ-2026-00012]: uom
 *
 * `item_code: null` IS accepted (verified the same way) — `uom: null` is not. And `verifyUoms`
 * (`api/useMailExtraction.ts`) blanks any UOM the `UOM` master doesn't recognise, which is common:
 * customers write CARTONS, DRUMS, packs. So without a substitution here, one odd unit fails the
 * WHOLE create, and the resolver that used to fix it lives on the RFQ builder — which the
 * purchaser cannot reach until the enquiry exists.
 *
 * ---- Why the item match is blanked along with it ----
 *
 * The dangerous case is not the unmatched line. It is a line that DID match an item and carries an
 * unresolvable UOM — "5 CARTONS of toothpaste" → `item_code` set, `uom` null. Substituting `Nos`
 * there produces a line that reads as fully resolved while meaning 5 pieces instead of 5 cartons.
 * That is a quantity error with money attached, and nothing on screen would hint at it until a
 * supplier quotes.
 *
 * So a substituted UOM always drags the item match down with it. The line arrives on the builder
 * as "needs matching", which guarantees a human looks at it before it can reach an RFQ. Losing a
 * correct item match costs one click; shipping a wrong quantity costs money.
 *
 * The customer's own words survive on `raw_text`/`description` either way — otherwise the
 * purchaser would be looking at OUR unit with no record of theirs.
 */

import { withoutInternalAnnotations } from '../../lib/internalNotes'
import type { ExtractedLine } from '../../lib/api/useMailExtraction'
import type { NewEnquiryItem } from '../../lib/types'

/**
 * Stands in for a UOM the master could not verify. **Not a reading of the customer's intent** —
 * it exists only because the field is mandatory. Any line carrying it is deliberately left
 * unmatched so it cannot be mistaken for a resolved value.
 */
export const PLACEHOLDER_UOM = 'Nos'

export function toCreateLine(line: ExtractedLine): NewEnquiryItem {
  const unresolvedUom = line.uom === null

  /*
    Keep what the customer actually wrote.

    `raw_uom` is the model's original unit, kept by `verifyUoms` precisely when the master could
    not verify it. The substitution below replaces it with `Nos`, so without this the information
    needed to resolve the line correctly is destroyed: "12 DRUMS gear oil" becomes `qty 12 · Nos`
    with nothing anywhere recording DRUMS. `description` does NOT save us — it is the model's
    description, and the unit is a separate extracted field it often does not repeat.
  */
  const originalText =
    unresolvedUom && line.raw_uom ? `${line.description} (customer's unit: ${line.raw_uom})` : line.description

  return {
    // Blanked when the UOM had to be substituted, even if the extraction matched an item — see
    // the module header for why that trade is the right way round.
    item_code: unresolvedUom ? null : line.item_code,
    qty: line.qty,
    uom: unresolvedUom ? PLACEHOLDER_UOM : line.uom,
    // Our notes go on `raw_text` only — the record the RFQ builder's labels read. The printed
    // description is the customer's words alone (Akhil, 2026-10-02: "don't ever append").
    raw_text: originalText,
    description: withoutInternalAnnotations(line.description),
    confidence: line.confidence,
    /*
      What the customer asked in — recorded at create and never changed afterwards (Akhil,
      2026-09-15: "customer_uom … in their uom format, it will not change"). ONLY the verified
      unit: `customer_uom` is a Link to the UOM master (measured 2026-09-17), and `raw_uom` is by
      definition a unit the master did not know — sent there it failed the whole create with a
      LinkValidationError, so the mail never became an enquiry. The customer's own word survives
      in the `(customer's unit: …)` annotation above; the purchaser's decision for such a line
      lands in `customer_uom` later, through `uomChoice.ts`.
    */
    ...(!unresolvedUom && line.uom ? { customer_uom: line.uom } : {}),
  }
}

/**
 * The inverse of the `(customer's unit: …)` suffix added above — for anything that needs the
 * customer's ACTUAL words rather than our annotation.
 *
 * Built for the catalogue matcher, where the annotation is not merely noise but actively
 * misleading. Measured against `bosun-search` 2026-08-12:
 *
 *   "A3 Laminator Machine for 125 Micron (customer's unit: Set)"
 *      → FILE HANDLE 125 MM (0.084) … the laminator third, at 0.053
 *   "A3 Laminator Machine for 125 Micron"
 *      → POUCH LAMINATOR 100V MAX WIDTH 325MM A3 (0.118) … correct, first
 *
 * The suffix pulls a vector search toward whatever else mentions units, and it is OUR text: the
 * customer never wrote it. Anything doing semantic work on a line should strip it first.
 */
export function withoutUnitAnnotation(text: string): string {
  return text.replace(/\s*\(customer's unit:[^)]*\)\s*$/i, '').trim()
}

/* ==============================================================================================
 * Splitting one attachment into more than one enquiry
 * ============================================================================================ */

export interface EnquiryDraft {
  /** The attachment these lines came from, when the mail carried more than one. */
  file: string | null
  /** The sheet within it, when the file had more than one carrying an order. */
  sheet: string | null
  /** What to show the purchaser: `req.xlsx`, `Deck`, or `req.xlsx · Deck`. */
  label: string
  lines: NewEnquiryItem[]
}

/**
 * Group extracted lines into the enquiries a purchaser might create from them.
 *
 * A workbook's sheets are often separate REQUISITIONS — deck on one, engine on the next, going to
 * different suppliers under different enquiry types — so the screen offers a choice. This is the
 * part of that choice worth testing on its own: which lines end up together.
 *
 * Order is preserved exactly. A purchaser checking a split against the workbook in front of them
 * reads down it, and a reordered list turns "is this right?" into a search.
 */
export function groupLinesBySheet(lines: readonly ExtractedLine[]): EnquiryDraft[] {
  const drafts: EnquiryDraft[] = []
  const byOrigin = new Map<string, EnquiryDraft>()

  for (const line of lines) {
    const file = line.file ?? null
    const sheet = line.sheet ?? null
    /*
      Keyed on BOTH. A mail can carry two requisition files, each of which can itself hold a deck
      sheet and an engine sheet — and every one of those is a separate requisition going to
      possibly different suppliers. Lines with neither (a PDF, a mail body) share one key and so
      form one draft, which is right: they came from one document.
    */
    const key = `${file ?? ''}\u0000${sheet ?? ''}`
    let draft = byOrigin.get(key)
    if (!draft) {
      draft = { file, sheet, label: originLabel(file, sheet), lines: [] }
      byOrigin.set(key, draft)
      drafts.push(draft)
    }
    draft.lines.push(toCreateLine(line))
  }

  return drafts
}

/**
 * What the purchaser reads on the choice.
 *
 * Both parts only when both exist and both are needed — "req.xlsx · Sheet1" for a single-sheet
 * file is noise, and noise is what stops people reading the prompts that matter.
 */
function originLabel(file: string | null, sheet: string | null): string {
  if (file && sheet) return `${file} · ${sheet}`
  return file ?? sheet ?? 'this mail'
}

/**
 * Whether splitting is even on the table.
 *
 * One draft means there is nothing to ask about — offering a choice with one option is noise, and
 * a purchaser who sees it once stops reading the ones that matter.
 */
export function canSplit(drafts: readonly EnquiryDraft[]): boolean {
  return drafts.length > 1
}


/**
 * The three ways a multi-sheet mail can become enquiries (Akhil, 2026-09-03).
 *
 * A workbook with a deck sheet and an engine sheet can mean three different things, and only the
 * purchaser knows which:
 *
 *   `per-sheet` — two separate orders travelling together. Two enquiries. The default, because a
 *                 split that was wrong is visible and mergeable, while a silent merge is invisible
 *                 until a supplier has been quoted for the wrong department.
 *   `combined`  — one order that ran onto a second sheet. One enquiry, every line.
 *   `one-sheet` — one sheet is the order; the rest is a price list, a covering note, last month's
 *                 copy. One enquiry from the chosen sheet only.
 *
 * `one-sheet` is the one that was missing: before it, quoting from a single sheet of a workbook
 * meant creating everything and deleting the rest by hand.
 */
export type SheetMode = 'per-sheet' | 'combined' | 'one-sheet'

export function draftsForMode(
  drafts: readonly EnquiryDraft[],
  mode: SheetMode,
  /** The `label` of the chosen draft. Only read in `one-sheet`. */
  chosen: string | null,
): EnquiryDraft[] {
  if (drafts.length <= 1) return [...drafts]

  if (mode === 'combined') {
    return [
      {
        // Neither identifies the merge: it spans several of each. The label carries what went in.
        file: null,
        sheet: null,
        // Named, not "this mail". The purchaser is about to confirm this, and the labels are the
        // only remaining record of which sheets were folded together.
        label: drafts.map((d) => d.label).join(' + '),
        // Workbook order, preserved — a purchaser checking the merge reads down the file.
        lines: drafts.flatMap((d) => d.lines),
      },
    ]
  }

  if (mode === 'one-sheet') {
    // No match yields NOTHING, never everything. Falling back to all of them would create an
    // enquiry the purchaser did not ask for, out of lines they were narrowing down; the bar
    // disables its button on an empty result instead.
    const one = drafts.find((d) => d.label === chosen)
    return one ? [one] : []
  }

  return [...drafts]
}
